import { useState, useEffect } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MapPicker } from "@/components/MapPicker";
import { Checkbox } from "@/components/ui/checkbox";
import PhoneInput from "react-phone-number-input";

interface Lead {
  _id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  postalCode?: string;
  city?: string;
  state?: string;
  country?: string;
  lat?: number;
  lng?: number;
  unsubscribedFromEmails?: boolean;
  instagram?: {
    igUserId?: string;
    igUsername?: string;
  };
}

export function ContactEdit() {
  const { id } = useParams<{ id: string }>();
  const { lead } = useOutletContext<{ lead: Lead }>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    instagramUsername: "",
    postalCode: "",
    city: "",
    state: "",
    country: "",
    lat: "",
    lng: "",
    unsubscribedFromEmails: false,
  });

  useEffect(() => {
    if (lead) {
      setFormData({
        email: lead.email || "",
        firstName: lead.firstName || "",
        lastName: lead.lastName || "",
        phoneNumber: lead.phoneNumber || "",
        instagramUsername: lead.instagram?.igUsername || "",
        postalCode: lead.postalCode || "",
        city: lead.city || "",
        state: lead.state || "",
        country: lead.country || "",
        lat: lead.lat?.toString() || "",
        lng: lead.lng?.toString() || "",
        unsubscribedFromEmails: lead.unsubscribedFromEmails ?? false,
      });
    }
  }, [lead]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationSelect = (lat: number, lng: number) => {
    setFormData((prev) => ({
      ...prev,
      lat: lat.toString(),
      lng: lng.toString(),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setIsSubmitting(true);
    try {
      const payload: any = {
        email: formData.email || "",
        firstName: formData.firstName || "",
        lastName: formData.lastName || "",
        phoneNumber: formData.phoneNumber || "",
        instagramUsername: formData.instagramUsername || "",
        postalCode: formData.postalCode || "",
        city: formData.city || "",
        state: formData.state || "",
        country: formData.country || "",
        unsubscribedFromEmails: formData.unsubscribedFromEmails,
      };

      payload.lat = formData.lat ? parseFloat(formData.lat) : "";
      payload.lng = formData.lng ? parseFloat(formData.lng) : "";

      const response = await fetch(`/api/leads/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        console.error("Failed to update lead:", data.error);
        alert(data.error || "Failed to update contact");
      }
    } catch (error) {
      console.error("Failed to update lead:", error);
      alert("An error occurred while updating the contact");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!lead) return null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name</Label>
          <Input
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="flex items-end gap-4">
        <div className="space-y-2 flex-1">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
          />
        </div>
        <div className="flex items-center space-x-2 pb-2">
          <Checkbox
            id="unsubscribedFromEmails"
            checked={formData.unsubscribedFromEmails}
            onCheckedChange={(checked) =>
              setFormData((prev) => ({
                ...prev,
                unsubscribedFromEmails: checked === true,
              }))
            }
          />
          <Label htmlFor="unsubscribedFromEmails" className="cursor-pointer">
            Unsubscribed from emails
          </Label>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="instagramUsername">Instagram username</Label>
        <Input
          id="instagramUsername"
          name="instagramUsername"
          placeholder="@handle or handle"
          autoComplete="off"
          value={formData.instagramUsername}
          onChange={handleChange}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phoneNumber">Phone Number</Label>
        <PhoneInput
          id="phoneNumber"
          defaultCountry="US"
          international={false}
          value={formData.phoneNumber}
          onChange={(value) => setFormData((prev) => ({ ...prev, phoneNumber: value || "" }))}
          className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="postalCode">Postal Code</Label>
        <Input
          id="postalCode"
          name="postalCode"
          value={formData.postalCode}
          onChange={handleChange}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="state">State</Label>
          <Input
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="country">Country</Label>
        <Input
          id="country"
          name="country"
          value={formData.country}
          onChange={handleChange}
        />
      </div>

      <div className="space-y-2">
        <Label>Location on Map</Label>
        <MapPicker
          lat={formData.lat ? (isNaN(parseFloat(formData.lat)) ? undefined : parseFloat(formData.lat)) : undefined}
          lng={formData.lng ? (isNaN(parseFloat(formData.lng)) ? undefined : parseFloat(formData.lng)) : undefined}
          onLocationSelect={handleLocationSelect}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="lat">Latitude</Label>
          <Input
            id="lat"
            name="lat"
            type="number"
            step="any"
            value={formData.lat}
            onChange={handleChange}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lng">Longitude</Label>
          <Input
            id="lng"
            name="lng"
            type="number"
            step="any"
            value={formData.lng}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
}
